import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Bell, Settings } from 'lucide-react';
import { clsx } from 'clsx';
import { businessApi, type Business } from '../../lib/api';
import { useSelectedBusinessId } from '../../hooks/useSelectedBusiness';

// -------------------------------------------------------------------
// Section segment → Russian label
// -------------------------------------------------------------------
const SECTION_LABELS: Record<string, string> = {
  dashboard: 'Обзор',
  reviews: 'Отзывы',
  services: 'Услуги',
  keywords: 'Ключи',
  content: 'Контент',
  reports: 'Отчёты',
  analytics: 'Аналитика',
  optimization: 'Оптимизация',
};

// Top-level route → single crumb label
const TOP_LEVEL_LABELS: Record<string, string> = {
  settings: 'Настройки',
  notifications: 'Уведомления',
  businesses: 'Все клиенты',
};

interface Crumb {
  label: string;
  href?: string;
}

function buildCrumbs(pathname: string, businessName: string | null): Crumb[] {
  // Strip leading slash and split
  const segments = pathname.replace(/^\//, '').split('/').filter(Boolean);

  if (segments.length === 0) {
    return [{ label: 'Все клиенты', href: '/businesses' }];
  }

  // /client/:id/<section>
  if (segments[0] === 'client' && segments.length >= 2) {
    const sectionSegment = segments[2] ?? '';
    const sectionLabel = SECTION_LABELS[sectionSegment] ?? sectionSegment;
    const crumbs: Crumb[] = [{ label: 'Все клиенты', href: '/businesses' }];
    crumbs.push({ label: businessName ?? 'Клиент' });
    if (sectionLabel) crumbs.push({ label: sectionLabel });
    return crumbs;
  }

  // Top-level named routes
  const topLabel = TOP_LEVEL_LABELS[segments[0]];
  if (topLabel) {
    return [{ label: topLabel }];
  }

  // Unknown path — sensible single crumb
  return [{ label: segments[0] }];
}

// -------------------------------------------------------------------
// Component
// -------------------------------------------------------------------
export function Topbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const selectedId = useSelectedBusinessId();

  // Business list for resolving client name
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [businessesLoaded, setBusinessesLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    businessApi
      .getAll()
      .then((res) => {
        if (!cancelled) {
          setBusinesses(res.data.data);
          setBusinessesLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) setBusinessesLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const businessName = businessesLoaded
    ? (businesses.find((b) => b.id === selectedId)?.name ?? null)
    : null;

  const crumbs = buildCrumbs(location.pathname, businessName);

  // -------------------------------------------------------------------
  // Avatar dropdown
  // -------------------------------------------------------------------
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowUserMenu(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  // -------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------
  return (
    <header className="h-[52px] bg-white border-b border-gray-200 sticky top-0 z-40 flex items-center justify-between px-6">
      {/* Left: breadcrumbs */}
      <nav aria-label="Breadcrumb">
        <ol className="flex items-center gap-1.5 text-sm">
          {crumbs.map((crumb, index) => {
            const isLast = index === crumbs.length - 1;
            return (
              <li key={index} className="flex items-center gap-1.5">
                {index > 0 && (
                  <span className="text-gray-300 select-none" aria-hidden>
                    /
                  </span>
                )}
                {crumb.href && !isLast ? (
                  <Link
                    to={crumb.href}
                    className="text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    {crumb.label}
                  </Link>
                ) : (
                  <span
                    className={clsx(
                      isLast ? 'text-gray-900 font-medium' : 'text-gray-500',
                    )}
                  >
                    {crumb.label}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </nav>

      {/* Right: bell + avatar */}
      <div className="flex items-center gap-2">
        {/* Bell → /notifications */}
        <button
          type="button"
          onClick={() => navigate('/notifications')}
          className="relative p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label="Уведомления"
        >
          <Bell className="w-5 h-5" />
        </button>

        {/* Avatar with account dropdown */}
        <div className="relative" ref={userMenuRef}>
          <button
            type="button"
            onClick={() => setShowUserMenu((prev) => !prev)}
            aria-haspopup="true"
            aria-expanded={showUserMenu}
            className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center text-white text-sm font-semibold hover:bg-primary-700 transition-colors"
            aria-label="Меню аккаунта"
          >
            А
          </button>

          {showUserMenu && (
            <div
              role="menu"
              className="absolute right-0 top-full mt-2 w-44 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setShowUserMenu(false);
                  navigate('/settings');
                }}
                className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <Settings className="w-4 h-4 text-gray-400" />
                Настройки
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
